// 執事系 統一料金モジュール（JSON読み取り化・CFM-J03 / REQ-PU-JBA-10〜13）
// 金額の唯一の真値 = src/data/butler-pricing-unified.json の amount_ex_tax（税別）。
// 税込表示額 = round_half_to_even(amount_ex_tax × (1 + taxRate))。
// taxRate と丸め規約は本JSON tax_policy を単一参照する（各ページで再定義しない）。
// site-thebutler と同一機構（同一正本JSON由来）。
import pricingJson from '../data/butler-pricing-unified.json';

// 参照に必要な最小形。JSONの真値そのものを型付きで取り出す（ハードコード額を持たない）。
interface PricingData {
  tax_policy: { taxRate: number };
  shared_plans: {
    'guest-footman': { amount_ex_tax: number; extension: { amount_ex_tax: number } };
    'day-butler': { amount_ex_tax: number };
    'private-butler': {
      variations: { amount_ex_tax: number }[];
      additional_day: { amount_ex_tax: number };
      extension: { amount_ex_tax: number };
    };
    'house-steward': { variations: { amount_ex_tax: number }[] };
  };
}

const pricing = pricingJson as unknown as PricingData;

const taxRate: number = pricing.tax_policy.taxRate;

/** 銀行家の丸め（round half to even・.5は偶数側）でJPY整数へ。JSON tax_policy.rounding_rule 準拠。 */
export function roundHalfToEven(value: number): number {
  const floor = Math.floor(value);
  const frac = value - floor;
  const EPS = 1e-9;
  if (frac < 0.5 - EPS) return floor;
  if (frac > 0.5 + EPS) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

/** 税別額(ex) → 税込表示額。JSON taxRate を単一参照。 */
export function incTax(ex: number): number {
  return roundHalfToEven(ex * (1 + taxRate));
}

/** 3桁区切り（カンマ）整形。例: 26400 → "26,400" */
export function formatYen(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** 税込額をカンマ整形して返すショートカット。例: yenInc(24000) → "26,400" */
export function yenInc(ex: number): string {
  return formatYen(incTax(ex));
}

// --- JBA手配プランの税別真値（JSON shared_plans 参照・ハードコード額なし） ---
const sp = pricing.shared_plans;

/** JBA手配プラン 税別基本額（JSON amount_ex_tax）。表示はすべて incTax/ yenInc 経由で税込化する。 */
export const jbaDispatchExTax = {
  /** ゲスト・フットマン / Footman 基本（3時間〜） = 24,000 */
  footmanBase: sp['guest-footman'].amount_ex_tax,
  /** フットマン 共通延長（円/時） = 8,000 */
  footmanExtension: sp['guest-footman'].extension.amount_ex_tax,
  /** デイ・バトラー / Butler（1日8時間） = 60,000 */
  butlerBase: sp['day-butler'].amount_ex_tax,
  /** バトラー 共通延長（円/時） = 8,000 */
  butlerExtension: sp['private-butler'].extension.amount_ex_tax,
  /** プライベート・バトラー / Second Steward 月20日×8時間 = 680,000 */
  secondStewardMonthly: sp['private-butler'].variations[0].amount_ex_tax,
  /** Second Steward 追加日（円/日） = 60,000 */
  secondStewardAddDay: sp['private-butler'].additional_day.amount_ex_tax,
  /** Second Steward 時間外・延長（円/時） = 8,000 */
  secondStewardExtension: sp['private-butler'].extension.amount_ex_tax,
  /** Second Steward 1日あたり換算（月額 ÷ 20日）税別 = 34,000 */
  secondStewardPerDay: sp['private-butler'].variations[0].amount_ex_tax / 20,
  /** ハウス・スチュワード / House Steward 24時間365日常駐（3名体制） = 6,000,000 */
  houseStewardMonthly: sp['house-steward'].variations[0].amount_ex_tax,
};
